import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";
import Breadcrumb from "../../components/shared/Breadcrumb";
import {
  createDirectorioEmpresa,
  deactivateDirectorioEmpresa,
  listDirectorioEmpresas,
  updateDirectorioEmpresa,
} from "../../platform/apiClient";
import { readSessionRole } from "../../platform/sessionContext";

const PAGE_SIZES = [10, 50, 100, 500];
const EMPTY_FORM = { rut: "", razon_social: "", nombre_fantasia: "", giro: "" };

function exportRows(companies) {
  return companies.map((company) => ({
    RUT: company.rut || "",
    "Razón social": company.razon_social || "",
    "Nombre fantasía": company.nombre_fantasia || "",
    Giro: company.giro || "",
    Estado: company.enabled ? "Activa" : "Desactivada",
  }));
}

function downloadBlob(content, filename, type) {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function escapeCsv(value) {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(companies) {
  const rows = exportRows(companies);
  const columns = Object.keys(rows[0] || { RUT: "", "Razón social": "", "Nombre fantasía": "", Giro: "", Estado: "" });
  const csv = [columns.join(","), ...rows.map((row) => columns.map((column) => escapeCsv(row[column])).join(","))].join("\n");
  downloadBlob(`\ufeff${csv}`, "empresas.csv", "text/csv;charset=utf-8;");
}

function downloadExcel(companies) {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(exportRows(companies));
  XLSX.utils.book_append_sheet(workbook, worksheet, "Empresas");
  XLSX.writeFile(workbook, "empresas.xlsx");
}

function pdfSafe(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7E]/g, "?")
    .replace(/[\\()]/g, "\\$&")
    .slice(0, 125);
}

function downloadPdf(companies) {
  const rows = exportRows(companies);
  const lines = [
    `Directorio de empresas - ${new Date().toLocaleDateString("es-CL")}`,
    "RUT | Razón social | Fantasía | Estado",
    ...rows.map((row) => `${row.RUT} | ${row["Razón social"]} | ${row["Nombre fantasía"]} | ${row.Estado}`),
  ];
  const chunks = [];
  for (let index = 0; index < lines.length; index += 48) chunks.push(lines.slice(index, index + 48));
  const firstPageObject = 3;
  const fontObject = firstPageObject + chunks.length * 2;
  const objects = [];
  objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[2] = `<< /Type /Pages /Kids [${chunks.map((_, index) => `${firstPageObject + index * 2} 0 R`).join(" ")}] /Count ${chunks.length} >>`;
  chunks.forEach((chunk, index) => {
    const pageObject = firstPageObject + index * 2;
    const contentObject = pageObject + 1;
    const content = [
      "BT",
      "/F1 9 Tf",
      "40 760 Td",
      ...chunk.map((line, lineIndex) => `${lineIndex ? "0 -14 Td " : ""}(${pdfSafe(line)}) Tj`),
      "ET",
    ].join("\n");
    objects[pageObject] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontObject} 0 R >> >> /Contents ${contentObject} 0 R >>`;
    objects[contentObject] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`;
  });
  objects[fontObject] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (let index = 1; index <= fontObject; index += 1) {
    offsets[index] = pdf.length;
    pdf += `${index} 0 obj\n${objects[index]}\nendobj\n`;
  }
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${fontObject + 1}\n0000000000 65535 f \n`;
  for (let index = 1; index <= fontObject; index += 1) pdf += `${String(offsets[index]).padStart(10, "0")} 00000 n \n`;
  pdf += `trailer\n<< /Size ${fontObject + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  downloadBlob(pdf, "empresas.pdf", "application/pdf");
}

export default function EmpresasPage() {
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modal, setModal] = useState(null);
  const canManage = ["admin", "superadmin"].includes(readSessionRole());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await listDirectorioEmpresas({ page, pageSize, search });
      setCompanies(response.empresas || []);
      setPagination(response.pagination || { total: 0 });
    } catch (reason) {
      setError(reason.message || "No se pudieron cargar las empresas.");
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / pageSize));

  async function exportAll(format) {
    setBusy(true);
    setError("");
    try {
      const all = [];
      let nextPage = 1;
      let total = 0;
      do {
        const response = await listDirectorioEmpresas({ page: nextPage, pageSize: 500, search });
        all.push(...(response.empresas || []));
        total = Number(response.pagination?.total || all.length);
        nextPage += 1;
      } while (all.length < total && nextPage < 100);
      if (format === "csv") downloadCsv(all);
      if (format === "excel") downloadExcel(all);
      if (format === "pdf") downloadPdf(all);
    } catch (reason) {
      setError(reason.message || "No se pudo generar la exportación.");
    } finally {
      setBusy(false);
    }
  }

  async function saveCompany(form) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      if (modal.mode === "edit") await updateDirectorioEmpresa(modal.company.id, form);
      else await createDirectorioEmpresa(form);
      setModal(null);
      setMessage(modal.mode === "edit" ? "Empresa actualizada correctamente." : "Empresa creada correctamente.");
      await load();
    } catch (reason) {
      setError(reason.message || "No se pudo guardar la empresa.");
    } finally {
      setBusy(false);
    }
  }

  async function deactivate(company) {
    if (!window.confirm(`¿Desactivar ${company.razon_social}?`)) return;
    setBusy(true);
    setError("");
    try {
      await deactivateDirectorioEmpresa(company.id);
      setMessage("Empresa desactivada correctamente.");
      await load();
    } catch (reason) {
      setError(reason.message || "No se pudo desactivar la empresa.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dashboard-main-body">
      <Breadcrumb title="Empresas" />
      {error && <div className="alert alert-danger mb-20">{error}</div>}
      {message && <div className="alert alert-success mb-20">{message}</div>}
      <div className="card basic-data-table">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h5 className="card-title mb-4">Directorio de empresas</h5>
            <span className="text-secondary-light">Empresas del tenant actual</span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {canManage && <button type="button" className="btn btn-primary d-flex align-items-center gap-2" onClick={() => setModal({ mode: "create", company: null })}><Icon icon="ic:baseline-add" />Nueva empresa</button>}
            {canManage && <Link to="/directorio/empresas/carga-masiva" className="btn btn-outline-primary d-flex align-items-center gap-2"><Icon icon="solar:upload-minimalistic-linear" />Carga masiva</Link>}
          </div>
        </div>
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-16">
            <div className="navbar-search flex-grow-1 max-w-420-px">
              <input type="text" className="bg-base h-44-px w-100" placeholder="Buscar por RUT, razón social, fantasía o giro" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
              <Icon icon="ion:search-outline" className="icon" />
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="text-sm text-secondary-light mb-0" htmlFor="company-page-size">Mostrar</label>
              <select id="company-page-size" className="form-select form-select-sm w-auto" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>
                {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
              <button type="button" className="btn btn-sm btn-outline-success" title="Exportar Excel" aria-label="Exportar Excel" onClick={() => exportAll("excel")} disabled={busy}><Icon icon="solar:file-download-linear" /></button>
              <button type="button" className="btn btn-sm btn-outline-secondary" title="Exportar CSV" aria-label="Exportar CSV" onClick={() => exportAll("csv")} disabled={busy}><Icon icon="solar:document-text-linear" /></button>
              <button type="button" className="btn btn-sm btn-outline-danger" title="Exportar PDF" aria-label="Exportar PDF" onClick={() => exportAll("pdf")} disabled={busy}><Icon icon="solar:file-download-linear" /></button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table bordered-table mb-0 align-middle">
              <thead><tr><th>RUT</th><th>Razón social</th><th>Nombre fantasía</th><th>Giro</th><th>Estado</th><th className="text-end">Acciones</th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan="6" className="text-center py-32">Cargando empresas...</td></tr>}
                {!loading && companies.map((company) => (
                  <tr key={company.id}>
                    <td>{company.rut || "—"}</td>
                    <td className="fw-semibold">{company.razon_social || "—"}</td>
                    <td>{company.nombre_fantasia || "—"}</td>
                    <td>{company.giro || "—"}</td>
                    <td><span className={`badge ${company.enabled ? "bg-success-50 text-success-600" : "bg-danger-50 text-danger-600"}`}>{company.enabled ? "Activa" : "Desactivada"}</span></td>
                    <td className="text-end">
                      <button type="button" className="btn btn-sm btn-outline-secondary me-2" title="Ver" onClick={() => setModal({ mode: "view", company })}><Icon icon="iconamoon:eye-light" /></button>
                      {canManage && <button type="button" className="btn btn-sm btn-outline-primary me-2" title="Editar" onClick={() => setModal({ mode: "edit", company })}><Icon icon="lucide:edit" /></button>}
                      {canManage && company.enabled && <button type="button" className="btn btn-sm btn-outline-danger" title="Desactivar" onClick={() => deactivate(company)} disabled={busy}><Icon icon="mingcute:delete-2-line" /></button>}
                    </td>
                  </tr>
                ))}
                {!loading && companies.length === 0 && <tr><td colSpan="6" className="text-center text-secondary-light py-32">No hay empresas para mostrar.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="d-flex align-items-center justify-content-between mt-16">
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))}>Anterior</button>
            <span className="text-sm text-secondary-light">Página {page} de {totalPages} · {pagination.total || 0} registros</span>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages || loading} onClick={() => setPage((value) => value + 1)}>Siguiente</button>
          </div>
        </div>
      </div>
      {modal && <CompanyModal mode={modal.mode} company={modal.company} busy={busy} onClose={() => setModal(null)} onSave={saveCompany} />}
    </div>
  );
}

function CompanyModal({ mode, company, busy, onClose, onSave }) {
  const readOnly = mode === "view";
  const [form, setForm] = useState({
    rut: company?.rut || "",
    razon_social: company?.razon_social || "",
    nombre_fantasia: company?.nombre_fantasia || "",
    giro: company?.giro || "",
  });
  const title = mode === "create" ? "Nueva empresa" : mode === "edit" ? "Editar empresa" : "Detalle de empresa";
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0, 0, 0, 0.45)" }} role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(event) => event.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header"><h6 className="modal-title">{title}</h6><button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} /></div>
          <form onSubmit={(event) => { event.preventDefault(); onSave(form); }}>
            <div className="modal-body"><div className="row gy-3">
              <Field label="RUT" value={form.rut} onChange={(value) => update("rut", value)} required disabled={readOnly} placeholder="76.123.456-7" />
              <Field label="Razón social" value={form.razon_social} onChange={(value) => update("razon_social", value)} required disabled={readOnly} />
              <Field label="Nombre de fantasía" value={form.nombre_fantasia} onChange={(value) => update("nombre_fantasia", value)} disabled={readOnly} />
              <Field label="Giro" value={form.giro} onChange={(value) => update("giro", value)} disabled={readOnly} />
            </div></div>
            <div className="modal-footer"><button type="button" className="btn btn-light" onClick={onClose}>Cerrar</button>{!readOnly && <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Guardando..." : "Guardar"}</button>}</div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required = false, disabled = false, placeholder = "" }) {
  return <div className="col-md-6"><label className="form-label">{label}{required ? " *" : ""}</label><input className="form-control" value={value} onChange={(event) => onChange(event.target.value)} required={required} disabled={disabled} placeholder={placeholder} /></div>;
}
